import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AopNav from './AopNav';
import AopSidebar from './AopSidebar';
import { aopApi } from './aopApi';
import Overview from './views/Overview';
import PendingActions from './views/PendingActions';
import WorkflowRuns from './views/WorkflowRuns';
import WorkflowRunDetail from './views/WorkflowRunDetail';
import AuditLog from './views/AuditLog';
import Reports from './views/Reports';
import ManualTrigger from './views/ManualTrigger';
import Agents from './views/Agents';
import McpTools from './views/McpTools';
import Settings from './views/Settings';
import PromptDocs from './views/PromptDocs';
import Learn from './views/Learn';
import './aop.css';

const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);
// Views backed by the AOP backend. Enabled in test environments (localhost +
// docsqa) but hidden on production (docs.upsellit.com) until the backend is
// ready for production.
const TEST_ONLY_VIEWS = new Set([
  'overview',
  'pending-actions',
  'workflow-runs',
  'workflow-run-detail',
  'audit-log',
  'reports',
  'manual-trigger',
  'agents',
  'learn',
  'settings',
]);

// Test environments: local dev hosts and the docsqa site. Production
// (docs.upsellit.com) does not contain "docsqa", so it stays gated.
function detectTestEnv() {
  if (typeof window === 'undefined') return false;
  const { hostname } = window.location;
  return LOCALHOST_HOSTNAMES.has(hostname) || hostname.includes('docsqa');
}

export default function AopApp({ user }) {
  const isTestEnv = useMemo(detectTestEnv, []);
  const defaultView = isTestEnv ? 'overview' : 'mcp-tools';
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const resolveView = (view) => {
    if (!isTestEnv && TEST_ONLY_VIEWS.has(view)) {
      return 'mcp-tools';
    }
    return view;
  };

  const currentView = resolveView(searchParams.get('view') || defaultView);
  const selectedRunId = searchParams.get('runId') || null;

  // Live queue/approval state for the sidebar badge and footer. Refreshed on
  // navigation so decisions made in a view are reflected immediately.
  useEffect(() => {
    if (!isTestEnv) return;
    aopApi
      .status()
      .then((res) => res.success && setStatus(res.status))
      .catch(() => setStatus(null));
  }, [isTestEnv, currentView]);

  const handleRunClick = (runId) => {
    if (!isTestEnv) return;
    setSearchParams({ view: 'workflow-run-detail', runId });
  };

  const handleViewChange = (view) => {
    const nextView = resolveView(view);
    if (nextView === 'workflow-run-detail') {
      setSearchParams({ view: nextView, ...(selectedRunId ? { runId: selectedRunId } : {}) });
    } else {
      setSearchParams({ view: nextView });
    }
    setMobileNavOpen(false);
  };

  const activeView = currentView;

  const renderView = () => {
    switch (activeView) {
      case 'overview':
        return <Overview onViewChange={handleViewChange} onRunClick={handleRunClick} />;
      case 'pending-actions':
        return <PendingActions />;
      case 'workflow-runs':
        return <WorkflowRuns onRunClick={handleRunClick} />;
      case 'workflow-run-detail':
        return (
          <WorkflowRunDetail
            runId={selectedRunId}
            onBack={() => handleViewChange('workflow-runs')}
          />
        );
      case 'audit-log':
        return <AuditLog />;
      case 'reports':
        return <Reports />;
      case 'manual-trigger':
        return <ManualTrigger />;
      case 'agents':
        return <Agents onViewChange={handleViewChange} />;
      case 'mcp-tools':
        return <McpTools isTestEnv={isTestEnv} />;
      case 'settings':
        return <Settings />;
      case 'prompt-docs':
        return <PromptDocs library="prompts" />;
      case 'skill-docs':
        return <PromptDocs library="skills" />;
      case 'learn':
        return <Learn />;
      default:
        return <Overview onViewChange={handleViewChange} onRunClick={handleRunClick} />;
    }
  };

  const sidebarView = activeView === 'workflow-run-detail' ? 'workflow-runs' : activeView;

  return (
    <div className="aop-app">
      <AopNav user={user} onMenuToggle={() => setMobileNavOpen((o) => !o)} />
      <div className="aop-body">
        <AopSidebar
          isTestEnv={isTestEnv}
          currentView={sidebarView}
          onViewChange={handleViewChange}
          status={status}
          mobileOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
        />
        <main className="aop-content">
          {renderView()}
        </main>
      </div>
    </div>
  );
}
